import React from 'react';
import { Grid, PrimaryButton } from '@bb-ui/react-library';
import { Refresh } from '@bb-ui/icons/dist/medium';
import { useTranslation } from 'react-i18next';
import { ExecutionDetailResponseBody } from '@lct-jobs/core';
import useRestApi from 'hooks/useRestApi';
import { useExecutionPolling } from 'hooks/useExecutionPolling';
import { ErrorMessage } from 'components/ErrorMessage';
import { LoadingIndicator } from 'components/LoadingIndicator';
import { PageTemplate } from 'components/PageTemplate';
import { RegistrationBox } from './components/RegistrationBox';
import { ExecutionBox } from './components/ExecutionBox';
import { JobChildrenBox } from './components/JobChildrenBox';
import { JobPipelineBox, hasStepEvents } from './components/JobPipelineBox';
import { JobLogsBox } from './components/JobLogsBox';
import { JobInputBox } from './components/JobInputBox';
import { JobOutputBox } from './components/JobOutputBox';
import { JobErrorBox } from './components/JobErrorBox';

export interface ExecutionDetailsViewProps {
  apiPath: string;
  title: string;
  testIdPrefix: string;
}

const CHILD_JOBS_LIMIT = 500;
const MAX_AUTO_LOAD_PAGES = 50;

export const ExecutionDetailsView: React.FC<ExecutionDetailsViewProps> = ({
  apiPath,
  title,
  testIdPrefix,
}) => {
  const { t } = useTranslation();
  const { data, loading, error, fetch } = useRestApi(apiPath);
  const executionDetails = React.useMemo<ExecutionDetailResponseBody>(() => data ?? [], [data]);

  const [childJobs, setChildJobs] = React.useState<any[]>([]);
  const [autoLoadCount, setAutoLoadCount] = React.useState(0);

  React.useEffect(() => {
    setChildJobs([]);
    setAutoLoadCount(0);
  }, [apiPath]);

  React.useEffect(() => {
    if (executionDetails?.children) {
      setChildJobs((prev) => {
        const existingIds = new Set(prev.map((child) => child.executionId));
        const newChildren = executionDetails.children.filter(
          (child) => !existingIds.has(child.executionId),
        );
        if (newChildren.length === 0) return prev;
        return [...prev, ...newChildren];
      });
    }
  }, [executionDetails, executionDetails?.children]);

  const combinedExecutionData = React.useMemo(() => {
    if (!data) return executionDetails;
    return {
      ...executionDetails,
      children: childJobs,
    };
  }, [executionDetails, childJobs, data]);

  const loadChildJobs = React.useCallback(() => {
    const nextPageToken = executionDetails?.nextPageToken;
    if (nextPageToken) {
      fetch({ params: { nextPageToken, limit: CHILD_JOBS_LIMIT } }).catch(() => {});
    }
  }, [executionDetails, fetch]);

  React.useEffect(() => {
    const nextPageToken = executionDetails?.nextPageToken;
    if (nextPageToken && !loading && autoLoadCount < MAX_AUTO_LOAD_PAGES) {
      setAutoLoadCount((prev) => prev + 1);
      loadChildJobs();
    }
  }, [executionDetails?.nextPageToken, loading, autoLoadCount, loadChildJobs]);

  useExecutionPolling(combinedExecutionData, fetch);

  const hasPipeline = hasStepEvents(combinedExecutionData?.logs);
  const logsBox = (
    <JobLogsBox jobExecution={combinedExecutionData} hasMore={!!executionDetails?.nextPageToken} />
  );

  let content: React.ReactElement;

  if (error) {
    content = (
      <ErrorMessage
        data-testid={`${testIdPrefix}-details-error`}
        title={t('jobService.loadError')}
        message={error.message}
        variant="block"
      />
    );
  } else if (loading) {
    content = <LoadingIndicator data-testid={`${testIdPrefix}-details-init`} />;
  } else {
    content = (
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 6 }}>
          <RegistrationBox jobExecution={combinedExecutionData} detailsLoading={loading} />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <ExecutionBox
            jobExecution={combinedExecutionData}
            detailsLoading={loading}
            onRescheduleSuccess={() => fetch().catch(() => {})}
            onCancelSuccess={() => fetch().catch(() => {})}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <JobChildrenBox
            jobExecution={combinedExecutionData}
            detailsLoading={loading}
            onLoadMore={loadChildJobs}
            hasMore={!!executionDetails?.nextPageToken}
          />
        </Grid>
        {/* Without step events the layout is unchanged: logs sit beside Input/Output. */}
        <Grid size={{ xs: 12, md: 6 }}>
          {hasPipeline ? <JobPipelineBox jobExecution={combinedExecutionData} /> : logsBox}
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <JobInputBox jobExecution={combinedExecutionData} />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <JobOutputBox jobExecution={combinedExecutionData} />
        </Grid>
        {combinedExecutionData?.exception && (
          <Grid size={12}>
            <JobErrorBox jobExecution={combinedExecutionData} />
          </Grid>
        )}
        {hasPipeline && <Grid size={{ xs: 12 }}>{logsBox}</Grid>}
      </Grid>
    );
  }

  return (
    <PageTemplate
      title={title}
      data-testid={`${testIdPrefix}-details-page`}
      headerControl={
        <PrimaryButton
          startIcon={<Refresh />}
          onClick={() => fetch().catch(() => {})}
          data-testid={`${testIdPrefix}-refresh-button`}
        >
          Refresh
        </PrimaryButton>
      }
    >
      {content}
    </PageTemplate>
  );
};

export default ExecutionDetailsView;
